import * as azure from "./azure";
import * as supabaseUsers from "./supabase/users";
import * as supabaseImages from "./supabase/images";
import * as supabaseLikes from "./supabase/likes";
import * as supabaseNotifications from "./supabase/notifications";
import * as supabaseMessages from "./supabase/messages";
import * as supabaseSubscriptions from "./supabase/subscriptions";
import * as supabaseAdminStats from "./supabase/adminStats";
import { isSupabaseBackend } from "./config";

function pick(supabaseFn, azureFn) {
  return (...args) => (isSupabaseBackend() ? supabaseFn(...args) : azureFn(...args));
}

export const GetAllUsers = pick(supabaseUsers.GetAllUsers, azure.GetAllUsers);
export const GetUserById = pick(supabaseUsers.GetUserById, azure.GetUserById);
export const CreateUser = pick(supabaseUsers.CreateUser, azure.CreateUser);
export const UpdateUser = pick(supabaseUsers.UpdateUser, azure.UpdateUser);
export const CompleteProfile = pick(supabaseUsers.CompleteProfile, azure.CompleteProfile);
export const GetUserAccountStatus = pick(supabaseUsers.GetUserAccountStatus, azure.GetUserAccountStatus);
const _GetUserRecommendations = pick(supabaseUsers.GetUserRecommendations, azure.GetUserRecommendations);
export const GetUserRecommendations = async (userId, options) => {
  const result = await _GetUserRecommendations(userId, options);
  if (Array.isArray(result)) {
    return {
      items: result,
      fetchedCount: result.length,
      hasMore: false,
    };
  }
  return result;
};
export const RECOMMENDATIONS_PAGE_SIZE = supabaseUsers.RECOMMENDATIONS_PAGE_SIZE ?? 40;
export const UpdateUserLocation = pick(supabaseUsers.UpdateUserLocation, azure.UpdateUserLocation);
export const UpdateUserPreferences = pick(supabaseUsers.UpdateUserPreferences, azure.UpdateUserPreferences);
export const GetUserPreferences = pick(supabaseUsers.GetUserPreferences, azure.GetUserPreferences);
export const DeleteUser = pick(supabaseUsers.DeleteUser, azure.DeleteUser);
export const DeactivateUser = pick(supabaseUsers.DeactivateUser, azure.DeactivateUser);
export const ActivateUser = pick(supabaseUsers.ActivateUser, azure.ActivateUser);
export const UpdateCountry = pick(supabaseUsers.UpdateCountry, azure.UpdateCountry);
export const RecordComplianceAttestation = pick(
  supabaseUsers.RecordComplianceAttestation,
  async () => null
);
export const BlockUser = pick(supabaseUsers.BlockUser, async () => ({ success: false }));
export const UnblockUser = pick(supabaseUsers.UnblockUser, async () => ({ success: false }));
export const GetBlockedUserIds = pick(supabaseUsers.GetBlockedUserIds, async () => []);
export const ReportUser = pick(supabaseUsers.ReportUser, async () => null);
export const ListUserReports = pick(supabaseUsers.ListUserReports, async () => []);
export const UpdateReportStatus = pick(supabaseUsers.UpdateReportStatus, async () => null);

export const GetUserImages = pick(supabaseImages.GetUserImages, azure.GetUserImages);
export const UploadImages = pick(supabaseImages.UploadImages, azure.UploadImages);
export const UploadProfileAvatar = pick(supabaseImages.UploadProfileAvatar, azure.UploadImages);
export const RemoveImage = pick(supabaseImages.RemoveImage, azure.RemoveImage);

export const AddLike = pick(supabaseLikes.AddLike, azure.AddLike);
export const AddDislike = pick(supabaseLikes.AddDislike, azure.AddDislike);
export const ClearMyDislikes = pick(
  supabaseLikes.ClearMyDislikes,
  async () => ({ success: true })
);
export const GetTodayLikeCount = pick(supabaseLikes.GetTodayLikeCount, azure.GetTodayLikeCount);
export const GetAllInterestsByUserId = pick(supabaseLikes.GetAllInterestsByUserId, azure.GetAllInterestsByUserId);
export const ToggleInterest = pick(supabaseLikes.ToggleInterest, azure.ToggleInterest);
export const CheckIfInterestExist = pick(supabaseLikes.CheckIfInterestExist, azure.CheckIfInterestExist);
export const AddMatch = pick(supabaseLikes.AddMatch, azure.AddMatch);

export const GetLikesNotifications = pick(supabaseNotifications.GetLikesNotifications, azure.GetLikesNotifications);
export const GetMatchesNotifications = pick(supabaseNotifications.GetMatchesNotifications, azure.GetMatchesNotifications);
export const DeleteNotification = pick(supabaseNotifications.DeleteNotification, azure.DeleteNotification);

export const GetUserConversations = pick(supabaseMessages.GetUserConversations, azure.GetUserConversations);
export const SendMessage = pick(supabaseMessages.SendMessage, azure.SendMessage);
export const GetConversationById = pick(supabaseMessages.GetConversationById, azure.GetConversationById);
export const GetBuiltConversationById = pick(
  supabaseMessages.GetBuiltConversationById,
  async () => null
);
export const GetMessagesByConversationId = pick(supabaseMessages.GetMessagesByConversationId, azure.GetMessagesByConversationId);

export const GetSubscriptionStatus = pick(supabaseSubscriptions.GetSubscriptionStatus, azure.GetSubscriptionStatus);
export const GetPremiumStatusForUser = pick(
  supabaseSubscriptions.GetPremiumStatusForUser,
  async () => ({ valid: false, stripeSubscriptionId: null })
);
export const grantManualPremium = pick(
  supabaseSubscriptions.grantManualPremium,
  async () => ({ success: false })
);
export const revokeManualPremium = pick(
  supabaseSubscriptions.revokeManualPremium,
  async () => ({ success: false })
);

export const GetAdminActivityStats = pick(
  supabaseAdminStats.GetAdminActivityStats,
  async () => ({
    likesTotal: 0,
    likesToday: 0,
    messagesTotal: 0,
    messagesToday: 0,
    matchesTotal: 0,
    matchesToday: 0,
  })
);

export {
  getUserPresence,
  getUserLastSeen,
  updateMyLastSeen,
  isUserOnline,
  ONLINE_THRESHOLD_MS,
} from "./supabase/presence";

export { isSupabaseBackend, BACKEND } from "./config";
export {
  subscribeToMessages,
  subscribeToConversations,
  markConversationRead,
} from "./supabase/messages";
